// The ATA CHAPTER field of a record's editor (Aaron 2026-10-09: "all components and
// inspections need a link to an ATA chapter"). A value / onChange control -- the caller
// writes the scalar `ata_chapter_id` (BP-08: bind the joined object, keep the id in sync),
// so it sits in a Formik form or a plain one alike.
//
// Options: every active chapter, read ONCE per page load (483 rows on g2/mustang/demo,
// 389 on peak; the list route pages at 100, so it walks the pages) and filtered on the
// client, as TypeAutocomplete does with its types. Sorted on major/minor code client-side:
// `chapter_code` is a computed property the route cannot sort on (see ./types.ts).
import React, { useEffect, useState } from "react";
import { Skeleton, TextField as MUITextField } from "@mui/material";
import MuiAutocomplete from "@mui/material/Autocomplete";

import { getATAChapters } from "./ata_chapters";
import { ataChapterLabel, chapterRefOf, type ATAChapterRow, type AtaChapterRef } from "./types";

const PAGE = 100;
let cached: Promise<AtaChapterRef[]> | null = null;

/** Every chapter as a ref, sorted by code. One walk of the pages per page load. */
export function loadAtaChapterOptions(): Promise<AtaChapterRef[]> {
  if (!cached) {
    cached = (async () => {
      const rows: ATAChapterRow[] = [];
      for (let page = 1; ; page += 1) {
        const { total, rows: got } = await getATAChapters({ page, size: PAGE });
        rows.push(...got);
        if (got.length < PAGE || rows.length >= total) break;
      }
      const key = (r: ATAChapterRow) => `${r.major_code ?? ""}-${r.minor_code ?? ""}`;
      return rows.sort((a, b) => key(a).localeCompare(key(b))).map(chapterRefOf);
    })();
    cached.catch(() => {
      cached = null; // a failed read is retried by the next editor that opens
    });
  }
  return cached;
}

/** Test seam: forget the page-load cache. */
export function resetAtaChapterOptions(): void {
  cached = null;
}

interface Props {
  value: AtaChapterRef | null | undefined;
  onChange: (chapter: AtaChapterRef | null) => void;
  label?: string;
  /** Under the field: what saving it does, e.g. "Applies to every item of this P/N". */
  helperText?: string;
  disabled?: boolean;
  name?: string;
}

export default function AtaChapterAutocomplete({
  value,
  onChange,
  label = "ATA Chapter",
  helperText,
  disabled = false,
  name = "ata_chapter",
}: Props) {
  const [options, setOptions] = useState<AtaChapterRef[]>();
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let live = true;
    loadAtaChapterOptions()
      .then((rows) => live && setOptions(rows))
      .catch(() => {
        if (live) {
          setFailed(true);
          setOptions([]);
        }
      });
    return () => {
      live = false;
    };
  }, []);

  if (!options) return <Skeleton animation="wave" sx={{ fontSize: "2rem" }} />;

  return (
    <MuiAutocomplete<AtaChapterRef, false, false, false>
      fullWidth
      autoHighlight
      options={options}
      value={value ?? null}
      disabled={disabled}
      onChange={(_, chapter) => onChange(chapter ?? null)}
      getOptionLabel={(option) => ataChapterLabel(option)}
      isOptionEqualToValue={(option, v) => option.id === v.id}
      renderInput={(params) => (
        <MUITextField
          {...params}
          name={name}
          label={label}
          variant="outlined"
          error={failed}
          helperText={failed ? "Could not load the ATA chapters" : helperText}
          inputProps={{ ...params.inputProps, "data-testid": `${name}-input` }}
        />
      )}
    />
  );
}
